import {
  PowerRebalanceSummaryResponse,
  FundRebalanceData,
  RebalanceType,
  SaveRiskProfileResponse,
  RebalanceQuestionnaireOTPTriggerResponse,
  RebalanceQuestionnaireOTPVerifyResponse
} from '../types/rebalance'
import { StatusResponse } from '../types/rebalance'
import { TaxCategories } from '../types/mutual-fund-detail.types'
import { trackEvent, pushScreen, openInBrowser } from '../utils/appBridge'
import { EventNames } from '../types/misc.types'

// Mock data for power rebalance summary
export const powerRebalanceSummaryMockData: PowerRebalanceSummaryResponse = {
  rebalancedFunds: [
    {
      isin: 'INF03VN01589',
      name: 'HDFC Equity Fund - Direct Plan - Growth',
      fundNature: 'EQUITY',
      fundCategory: 'LARGE_CAP',
      purchaseMode: 'DIRECT',
      powerRank: 3,
      performanceRating: 'UNDER_PERFORMING',
      missedGains: 125000,
      highExposure: false,
      mfLogoUrl: '/images/hdfc.png',
      sipAmount: 5000,
      showConsentBottomSheet: false
    },
    {
      isin: 'INF03VN01590',
      name: 'ICICI Prudential Bluechip Fund - Direct Plan - Growth',
      fundNature: 'EQUITY',
      fundCategory: 'LARGE_CAP',
      purchaseMode: 'DIRECT',
      powerRank: 2,
      performanceRating: 'ON_TRACK',
      missedGains: 85000,
      highExposure: true,
      mfLogoUrl: '/images/icici.png',
      sipAmount: 10000,
      showConsentBottomSheet: true
    },
    {
      isin: 'INF03VN01591',
      name: 'Axis Midcap Fund - Direct Plan - Growth',
      fundNature: 'EQUITY',
      fundCategory: 'MID_CAP',
      purchaseMode: 'DIRECT',
      powerRank: 4,
      performanceRating: 'UNDER_PERFORMING',
      missedGains: 95000,
      highExposure: false,
      mfLogoUrl: '/images/axis-bank-header.png',
      sipAmount: 7500
    }
  ],
  fundsExcludedFromRebalancing: [
    {
      isin: 'INF03VN01592',
      name: 'SBI Small Cap Fund - Direct Plan - Growth',
      fundNature: 'EQUITY',
      fundCategory: 'SMALL_CAP',
      purchaseMode: 'DIRECT',
      powerRank: 1,
      performanceRating: 'OUT_PERFORMING',
      missedGains: 0,
      highExposure: false,
      mfLogoUrl: '/images/motilal-oswal.png',
      exclusionReason: 'Fund is performing well and meets investment goals'
    }
  ],
  rebalanceExecutedFunds: [
    {
      isin: 'INF03VN01593',
      name: 'UTI Equity Fund - Direct Plan - Growth',
      fundNature: 'EQUITY',
      fundCategory: 'FLEXI_CAP',
      purchaseMode: 'DIRECT',
      powerRank: 5,
      performanceRating: 'UNDER_PERFORMING',
      missedGains: 0,
      highExposure: false,
      mfLogoUrl: '/images/motilal-oswal.png'
    }
  ],
  rebalanceEvaluationRefetchDate: Date.now() - 7 * 24 * 60 * 60 * 1000, // 7 days ago
  riskProfile: 'MODERATE',
  planStartDate: Date.now(),
  planDurationInMonths: 3,
  throughFamilyAccess: true,
  primaryUserName: 'Gaurav Singh',
  secondaryUserPhoneNumber: '8696507688',
  secondaryUserName: 'Harshita Singh Shaktawat',
  familyConsentText:
    "I, Harshita Singh Shaktawat, give consent to complete my Elite KYC from this phone (an OTP will be sent to Harshita Singh Shaktawat's mobile number)"
}

// Mock data for power rebalance ISIN (individual fund details)
export const getPowerRebalanceISINMockData = (isin: string): FundRebalanceData => {
  return {
    rebalanceTransferType: 'LUMPSUM_HIGH_EXPOSURE',
    rebalanceStrategyType: RebalanceType.LUMPSUM_HIGH_EXPOSURE,
    isin: isin,
    name: 'ICICI Prudential Bluechip Fund - Direct Plan - Growth',
    fundNature: 'EQUITY',
    fundCategory: 'LARGE_CAP',
    mfLogoUrl: '/images/icici.png',
    powerRank: 2,
    performanceRating: 'ON_TRACK',
    purchaseMode: 'DIRECT',
    missedGains: 125000,
    investedAmount: 500000,
    currentWealth: 625000,
    isHighExposureFund: true,
    highExposureThreshold: 30,
    highExposureTransferAmount: 200000,
    isElss: false,
    exclusionReason: '',
    switchSuggestions: [
      {
        isin: 'INF03VN01594',
        name: 'Parag Parikh Flexi Cap Fund - Direct Plan - Growth',
        switchPercentage: 100,
        fundNature: 'EQUITY',
        fundCategory: 'FLEXI_CAP',
        mfLogoUrl: '/images/motilal-oswal.png',
        powerRank: 1,
        performanceRating: 'OUT_PERFORMING',
        purchaseMode: 'DIRECT',
        investedAmount: null,
        currentWealth: null,
        reasonsToSwitch: {
          xirrDiff: 3.5,
          xirrDiffDurationInMonths: 36
        }
      }
    ],
    stepsToRebalance: {
      swpAmountPerMonth: 50000,
      swpDurationInMonths: 4
    },
    taxDetails: {
      stcgTaxInfo: {
        gains: 25000,
        investedAmount: 500000,
        taxRate: 0.15,
        taxableAmount: 3750
      },
      ltcgTaxInfo: {
        gains: 100000,
        investedAmount: 500000,
        taxRate: 0.1,
        taxableAmount: 10000
      },
      exitLoad: 0.01,
      currentValue: 625000,
      netTaxLiability: 13750,
      taxCategory: TaxCategories.NON_EQUITY
    },
    missedGainsAsOfDate: Date.now(),
    recommendationDate: Date.now(),
    // Legacy fields for backward compatibility
    elss: false,
    highExposureFund: true
  }
}

// Mock data for smart rebalance (status response)
export const smartRebalanceMockData: StatusResponse = {
  timeToRebalancePlanDrop: Date.now() + 5 * 24 * 60 * 60 * 1000, // 5 days from now
  numberOfFundsToRebalance: 2,
  missedGains: 305000,
  rebalanceAvailable: true,
  firstPlanMonth: new Date().getMonth() + 1,
  nextPlanMonth: new Date().getMonth() + 1,
  riskProfile: 'null',
  userEligibleForRebalance: true,
  numberOfFundsExcludedFromRebalance: 1,
  planDurationInMonths: 3,
  rebalancePlanCount: 2,
  throughFamilyAccess: true,
  userSubscriptionDetails: {
    specialOffering: true,
    signupOffer: true,
    offerStartTime: Date.now() - 86_400_000,
    offerExpiryTime: new Date().setHours(23, 59, 59, 999),
    offerType: 'SIGNUP_TENURE_BASED_OFFER'
  },
  primaryUserName: 'Tejas Singh Upadhyay',
  secondaryUserPhoneNumber: '9876543210',
  secondaryUserName: 'Anshu Singh Upadhyay',
  familyConsentText:
    'I, Anshukumar Singh, give consent to complete my risk assessment from this phone (to authorize this, an OTP will be sent to Anshukumar’s mobile number)'
}

/** Smart rebalance mock: signup tenure offer with <1 day remaining (live countdown). */
export const smartRebalanceSignupTenureCountdownMockData: StatusResponse = {
  ...smartRebalanceMockData,
  userSubscriptionDetails: {
    specialOffering: true,
    signupOffer: true,
    offerStartTime: Date.now() - 86_400_000,
    offerExpiryTime: Date.now() + 6 * 60 * 60 * 1000,
    offerType: 'SIGNUP_TENURE_BASED_OFFER'
  }
}

// Mock data for save risk profile response
export const saveRiskProfileMockData: SaveRiskProfileResponse = {
  success: true,
  message: 'Risk profile saved successfully'
}

// Data for rebalance cost banner
export interface RebalanceCostBannerData {
  leftIcon: string
  text: string
  rightIcon: string
  onClick: () => void
}

// Handler functions for rebalance cost banner
const handlePushScreen = () => {
  trackEvent({
    eventName: EventNames.UserClicked,
    payload: {
      screenType: 'MF_Smart_Rebalancing',
      eventId: 'Banner_Power_Of_Rebalancing'
    }
  })
  pushScreen('/v2/portfolio/web-redirection/power-of-rebalancing?from=APP')
}

const openBlogLink = () => {
  const blogUrl = `https://www.powerup.money/blog/mutual-funds/%f0%9f%94%b6-powerup-elite-highest-quality-advisory-lowest-priced-in-the-market`
  trackEvent({
    eventName: EventNames.UserClicked,
    payload: {
      screenType: 'MF_Smart_Rebalancing',
      eventId: 'ReblancingBlogClicked'
    }
  })
  openInBrowser(blogUrl)
}

export const rebalanceCostBannerData: RebalanceCostBannerData[] = [
  {
    leftIcon: `<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M13.5761 8.99408C13.9016 8.66866 14.4291 8.66866 14.7545 8.99408L17.6711 11.9107C17.9965 12.2362 17.9965 12.7637 17.6711 13.0892L14.7545 16.0058C14.4291 16.3312 13.9016 16.3312 13.5761 16.0058C13.2506 15.6803 13.2507 15.1528 13.5761 14.8274L15.0702 13.3332H9.99863C9.53838 13.3332 9.1653 12.9602 9.1653 12.4999C9.1653 12.0397 9.53838 11.6666 9.99863 11.6666H15.0702L13.5761 10.1725C13.2506 9.84699 13.2507 9.31949 13.5761 8.99408ZM5.24277 3.99407C5.5682 3.66864 6.09572 3.66865 6.42115 3.99407C6.74652 4.31951 6.74657 4.84704 6.42115 5.17245L4.92701 6.66659H9.99863C10.4588 6.66663 10.832 7.03972 10.832 7.49993C10.832 7.96012 10.4588 8.33324 9.99863 8.33324H4.92701L6.42115 9.82741C6.74652 10.1528 6.74657 10.6804 6.42115 11.0058C6.09573 11.3312 5.56821 11.3312 5.24277 11.0058L2.3261 8.08912C2.00068 7.76369 2.00067 7.23617 2.3261 6.91074L5.24277 3.99407Z'
      fill='#B86742'
    />
  </svg>`,
    text: "What's the cost of not rebalancing?",
    rightIcon: `<svg xmlns='http://www.w3.org/2000/svg' width='18' height='22' viewBox='0 0 18 22' fill='none'>
    <path
      d='M3.5 11L14.5195 11'
      stroke='#C17B58'
      stroke-width='2'
      stroke-linecap='round'
      stroke-linejoin='round'
    />
    <path
      d='M11.0197 14.5L14.5195 11L11 7.48047'
      stroke='#C17B58'
      stroke-width='2'
      stroke-linecap='round'
      stroke-linejoin='round'
    />
  </svg>`,
    onClick: handlePushScreen
  }
  //   {
  //     leftIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  //   <path d="M7.35938 3.63574L9.99805 0.998047L12.6367 3.63574L9.99805 6.27344L7.35938 3.63574ZM13.7227 10.0029L16.3604 7.36621H12.6406V3.63672H16.3711V7.36621H16.3623L19 10.0029L16.3613 12.6406L13.7227 10.0029ZM1 10.0029L3.6377 7.36621H3.62891V3.63672H7.35938V7.36621H3.63965L6.27734 10.0029L3.63867 12.6406L1 10.0029ZM12.6406 12.6445H16.3711V16.374H12.6406V12.6445ZM3.62891 12.6445H7.35938V16.374H3.62891V12.6445ZM7.35938 16.3623L9.99805 13.7246L12.6367 16.3623L9.99805 19L7.35938 16.3623Z" fill="#B86742"/>
  // </svg>`,
  //     text: `High quality advisory, at fraction of a cost`,
  //     rightIcon: `<svg xmlns='http://www.w3.org/2000/svg' width='18' height='22' viewBox='0 0 18 22' fill='none'>
  //     <path
  //       d='M3.5 11L14.5195 11'
  //       stroke='#C17B58'
  //       stroke-width='2'
  //       stroke-linecap='round'
  //       stroke-linejoin='round'
  //     />
  //     <path
  //       d='M11.0197 14.5L14.5195 11L11 7.48047'
  //       stroke='#C17B58'
  //       stroke-width='2'
  //       stroke-linecap='round'
  //       stroke-linejoin='round'
  //     />
  //   </svg>`,
  //     onClick: openBlogLink
  //   }
]

// Mock data for rebalance questionnaire OTP trigger
export const rebalanceQuestionnaireOTPTriggerMockData: RebalanceQuestionnaireOTPTriggerResponse = {}

// Mock data for rebalance questionnaire OTP verify
export const rebalanceQuestionnaireOTPVerifyMockData: RebalanceQuestionnaireOTPVerifyResponse = {
  verified: true,
  message: 'OTP verified successfully'
}
